import { DialogTitle } from "@headlessui/react";
import { Checkbox } from "components/CheckboxForm";
import Button from "components/buttons/Button";
import Modal from "components/modal/Modal";
import { AlertTriangleIcon } from "lucide-react";
import { InstructionHtmlBody } from "pages/project/common/taskBase/sidebar/instructions/InstructionHtmlBody";
import { instructionItemShellClassName } from "pages/project/common/taskBase/sidebar/instructions/instructionItemStyles";
import { InstructionAckContext } from "pages/project/common/taskBase/sidebar/instructions/useMandatoryInstructionAck";
import { TaskInstruction } from "pages/project/task/task.typing";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/ui";

interface Props {
  isShown: boolean;
  context: InstructionAckContext;
  instructions: TaskInstruction[];
  isConfirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** Navigate to the Instructions tab; the modal closes itself afterwards. */
  onViewAllInstructions: () => void;
}

export const MandatoryInstructionsModal = ({
  isShown,
  context,
  instructions,
  isConfirming,
  onCancel,
  onConfirm,
  onViewAllInstructions,
}: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "task.detail.instructions.acknowledgement" });
  const [isConfirmed, setIsConfirmed] = useState(false);

  useEffect(() => {
    if (isShown) {
      setIsConfirmed(false);
    }
  }, [isShown]);

  return (
    <Modal isShown={isShown} onClose={onCancel}>
      <div data-test="mandatory-instructions-modal">
        <div className="flex items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/40">
            <AlertTriangleIcon className="size-5 text-red-600 dark:text-red-400" aria-hidden="true" />
          </div>
          <DialogTitle as="h3" className="text-color-primary text-lg font-semibold leading-6">
            {t("title")}
          </DialogTitle>
        </div>

        <p className="text-color-secondary mt-3 text-sm">
          {context === "download" ? t("context-download") : t("context-cat")}
        </p>

        <div className="mt-4 max-h-64 space-y-2 overflow-y-auto">
          {instructions.map((instruction, index) => (
            <div
              key={`${instruction.source}-${index}`}
              data-test="mandatory-instruction-item"
              className={cn(instructionItemShellClassName, "border-l-red-500 dark:border-l-red-400")}
            >
              <InstructionHtmlBody instruction={instruction} className="text-color-primary" />
            </div>
          ))}
        </div>

        <button
          type="button"
          data-test="btn-view-all-instructions"
          className="mt-4 text-sm font-medium text-ribbon-500 hover:underline dark:text-ribbon-400"
          onClick={() => {
            onViewAllInstructions();
            onCancel();
          }}
        >
          {t("view-all")}
        </button>

        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-900/20">
          <Checkbox
            id="mandatory-instructions-confirmation"
            dataTestId="mandatory-instructions-confirmation"
            label={t("checkbox")}
            checked={isConfirmed}
            onChange={(event) => setIsConfirmed(event.target.checked)}
          />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-6">
          <Button color="ternary" className="w-full" label={t("cancel")} onClick={onCancel} />
          <Button
            color="primary"
            className="w-full"
            dataTestId="btn-confirm-instructions"
            disabled={!isConfirmed || isConfirming}
            label={t("confirm")}
            onClick={onConfirm}
          />
        </div>
      </div>
    </Modal>
  );
};
